import {
    initFlowbiteComponents,
    setupCroppieFlow,
    destroyCroppie,
    initLegacySnippets,
    initTomSelect,
    initStickyDayNav,
} from './ui-utils.js';

/* =================================================================
   HLAVNÍ INIT FUNKCE
   Tuto funkci voláme z app.js (start) a z naja-setup.js (po ajaxu).
================================================================= */
function markAdminEntitySelects(root = document) {
    if (!document.body?.matches('[data-admin-ui]')) return;
    const entityNames = /(^|\[)(team_id|home_team_id|away_team_id|player_id|primary_player_id|secondary_player_id|club_id|loan_from_club_id|competition_id|competition_ids|season_id|copy_from_season_id|stadium_id|prediction_contest_id|user_id|achievement_id|achievementId|plan_id|channel_id|favorite_team_id|formation_id)(\]|$)/;
    root.querySelectorAll('select').forEach((select) => {
        if (select.dataset.nativeSelect === '1' || select.dataset.tomSelect != null || !entityNames.test(select.name || select.id || '')) return;
        select.dataset.tomSelect = '1';
        select.classList.add('ts-field');
        if (!select.dataset.tomPlaceholder) {
            select.dataset.tomPlaceholder = select.multiple ? 'Vyber položky' : 'Vyber položku';
        }
    });
}


function initEventTeamPickers(root = document) {
    const pickers = root.querySelectorAll?.('[data-event-team-picker]') || [];
    pickers.forEach((picker) => {
        if (picker.dataset.eventTeamPickerReady === '1') return;
        const form = picker.closest('form');
        const select = form?.querySelector('[data-event-team-select]');
        if (!select) return;

        const buttons = Array.from(picker.querySelectorAll('[data-event-team-value]'));
        const sync = () => {
            const current = String(select.value || '');
            buttons.forEach((button) => {
                const active = String(button.dataset.eventTeamValue || '') === current;
                button.setAttribute('aria-pressed', active ? 'true' : 'false');
                button.classList.toggle('border-rose-400', active);
                button.classList.toggle('bg-rose-50', active);
                button.classList.toggle('ring-2', active);
                button.classList.toggle('ring-rose-100', active);
                button.classList.toggle('text-rose-700', active);
                button.classList.toggle('border-slate-200', !active);
                button.classList.toggle('bg-white', !active);
            });
        };

        buttons.forEach((button) => {
            button.addEventListener('click', () => {
                select.value = String(button.dataset.eventTeamValue || '');
                select.dispatchEvent(new Event('change', { bubbles: true }));
                sync();
            });
        });
        select.addEventListener('change', sync);
        picker.dataset.eventTeamPickerReady = '1';
        sync();
    });
}

export function initAllPageComponents(root = document) {
    // console.log('🔄 --- REINIT UI START ---');

    // 1. Úklid (Clean up)
    // Musíme zničit staré instance, aby se nehromadily v paměti.
    destroyCroppie(root);

    // 2. Základní knihovny
    initLegacySnippets(root);
    initFlowbiteComponents(root);

    // 3. Specifické prvky
    initThemeToggle(root);
    initPlayerTabs(root);
    initHamburger(root);
    initClipboardButtons(root);
    initReferralCopyFeedback(root);
    initUniversalFlashMessages(root);
    initNativeDropdowns(root);
    initNotificationPolling(root);
    initLiveClocks(root);
    markAdminEntitySelects(root);
    initTomSelect(['[data-tom-select]'], root);
    initEventTeamPickers(root);
    initSeasonCompetitionSelects(root);
    initHomeStadiumSelects(root);
    initStickyDayNav(root);
    initMediaImageFallbacks(root);
    initRegistrationIdentityChecks(root);
    initPlayerAvailabilityEditor(root);
    initHomepageInfoPrompt(root);
    initHomepageStudioRefresh(root);
    initSupportPaymentPicker(root);

    // 4. Croppie (načte se jen pokud existují prvky)
    setupCroppieFlow(root);


    // console.log('✅ --- REINIT UI DONE ---');
}








function initSupportPaymentPicker(root = document) {
    const request = url => {
        if (!window.naja) {
            window.location.href = url;
            return;
        }

        window.naja.makeRequest('GET', url, null, { history: false });
    };

    root.querySelectorAll?.('[data-support-amount-link]').forEach(link => {
        if (link.dataset.supportAmountReady === '1') return;
        link.dataset.supportAmountReady = '1';
        link.addEventListener('click', event => {
            event.preventDefault();
            if (!link.href) return;
            request(link.href);
        });
    });

    root.querySelectorAll?.('[data-support-custom-form]').forEach(form => {
        if (form.dataset.supportCustomReady === '1') return;
        form.dataset.supportCustomReady = '1';
        form.addEventListener('submit', event => {
            event.preventDefault();

            const url = new URL(form.action || window.location.href, window.location.href);
            const data = new FormData(form);
            for (const [name, value] of data.entries()) {
                url.searchParams.set(name, String(value));
            }
            url.hash = '';
            request(url.toString());
        });
    });
}

function initHomepageStudioRefresh(root = document) {
    const studio = root.querySelector?.('[data-homepage-studio-live]')
        || document.querySelector('[data-homepage-studio-live]');
    if (!studio || studio.dataset.homepageStudioRefreshReady === '1') return;

    studio.dataset.homepageStudioRefreshReady = '1';
    const refreshUrl = studio.dataset.homepageStudioRefreshUrl;
    if (!refreshUrl) return;

    const refreshMs = Math.max(5000, Number(studio.dataset.homepageStudioRefreshSeconds || 10) * 1000);
    let intervalId = null;

    const requestRefresh = () => {
        if (document.hidden || studio.dataset.homepageStudioRefreshEnabled !== '1' || !window.naja) return;
        window.naja.makeRequest('GET', refreshUrl, null, { history: false }).catch(() => {});
    };

    const startPolling = () => {
        if (intervalId !== null) return;
        studio.dataset.homepageStudioRefreshEnabled = '1';
        requestRefresh();
        intervalId = window.setInterval(requestRefresh, refreshMs);
    };

    const waitForKickoff = () => {
        if (studio.dataset.homepageStudioRefreshEnabled === '1') {
            startPolling();
            return;
        }

        const kickoffMs = Number(studio.dataset.homepageStudioKickoffMs || 0);
        if (!Number.isFinite(kickoffMs) || kickoffMs <= 0) return;

        const delay = kickoffMs - Date.now();
        if (delay <= 0) {
            startPolling();
            return;
        }

        window.setTimeout(waitForKickoff, Math.min(delay, 2_000_000_000));
    };

    waitForKickoff();
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden && Date.now() >= Number(studio.dataset.homepageStudioKickoffMs || Infinity)) {
            startPolling();
        }
    });
}

function initHomepageInfoPrompt(root = document) {
    const cards = root.querySelectorAll?.('[data-homepage-info-prompt]') || [];

    cards.forEach((card) => {
        if (card.dataset.homepageInfoInitialized === '1') return;
        card.dataset.homepageInfoInitialized = '1';

        const persistentSnooze = card.dataset.homepageInfoPersistentSnooze === '1';
        const key = persistentSnooze ? (card.dataset.homepageInfoSnoozeKey || '') : '';
        const minutes = Math.max(1, Number(card.dataset.homepageInfoSnoozeMinutes || 8));

        let snoozed = false;
        if (persistentSnooze) {
            try {
                const until = Number(window.localStorage.getItem(key) || 0);
                snoozed = key !== '' && until > Date.now();
                if (!snoozed && key !== '') window.localStorage.removeItem(key);
            } catch (_) {
                // Storage může být zablokované. Výzva pak zůstane dostupná.
            }
        }

        // Klasický reload vyřeší synchronní preflight před prvním paintem.
        // Tohle je pojistka pro AJAXové vložení nebo návrat z bfcache.
        if (snoozed) {
            card.remove();
            if (!document.querySelector('[data-homepage-info-prompt]')) {
                delete document.documentElement.dataset.homepageInfoSnoozed;
            }
            return;
        }

        if (document.documentElement.dataset.homepageInfoSnoozed === '0') {
            delete document.documentElement.dataset.homepageInfoSnoozed;
        }

        const dismiss = card.querySelector('[data-homepage-info-dismiss]');
        dismiss?.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (card.dataset.homepageInfoClosing === '1') return;
            card.dataset.homepageInfoClosing = '1';
            dismiss.disabled = true;
            card.setAttribute('aria-hidden', 'true');

            if (persistentSnooze) {
                try {
                    if (key) {
                        window.localStorage.setItem(key, String(Date.now() + minutes * 60_000));
                    }
                } catch (_) {
                    // Zavření funguje i bez persistence; jen se po další navigaci může vrátit.
                }
            }

            const removeCard = () => {
                if (!card.isConnected) return;
                card.remove();
            };

            card.addEventListener('transitionend', (transitionEvent) => {
                if (transitionEvent.target !== card) return;
                if (transitionEvent.propertyName !== 'grid-template-rows') return;
                removeCard();
            }, { once: true });

            card.classList.add('is-closing');
            window.setTimeout(removeCard, 440);
        });
    });
}

function initPlayerAvailabilityEditor(root = document) {
    const editors = root.querySelectorAll?.('[data-availability-editor]') || [];

    editors.forEach((editor) => {
        if (editor.dataset.availabilityInitialized === '1') return;
        editor.dataset.availabilityInitialized = '1';

        const form = editor.querySelector('[data-availability-form]');
        if (!form) return;

        const field = (name) => form.querySelector(`[name="${name}"], [name$="[${name}]"]`);
        const idField = field('availability_id');
        const teamField = field('team_id');
        const statusField = field('status');
        const startsField = field('starts_at');
        const endsField = field('ends_at');
        const noteField = field('note');
        const activeField = field('is_active');
        const submit = editor.querySelector('[data-availability-submit]');
        const heading = editor.querySelector('[data-availability-heading]');
        const icon = editor.querySelector('[data-availability-edit-icon]');
        const cancel = editor.querySelector('[data-availability-cancel]');

        const defaults = {
            team: '',
            status: 'injured',
            starts: editor.dataset.availabilityDefaultStart || startsField?.value || '',
            ends: '',
            note: '',
            active: true,
        };

        const setSelectValue = (select, value) => {
            if (!select) return;
            const normalized = value == null ? '' : String(value);
            if (select.tomselect) {
                select.tomselect.setValue(normalized, true);
            } else {
                select.value = normalized;
                select.dispatchEvent(new Event('change', { bubbles: true }));
            }
        };

        const setMode = (editing) => {
            if (heading) heading.textContent = editing ? 'Upravit evidovaný stav' : 'Přidat nový stav';
            if (submit) submit.value = editing ? 'Uložit změny' : 'Přidat stav';
            icon?.classList.toggle('hidden', !editing);
            cancel?.classList.toggle('hidden', !editing);
        };

        const resetEditor = () => {
            if (idField) idField.value = '0';
            setSelectValue(teamField, defaults.team);
            setSelectValue(statusField, defaults.status || 'injured');
            if (startsField) startsField.value = defaults.starts;
            if (endsField) endsField.value = defaults.ends;
            if (noteField) noteField.value = defaults.note;
            if (activeField) activeField.checked = defaults.active;
            setMode(false);
        };

        editor.querySelectorAll('[data-availability-edit]').forEach((trigger) => {
            trigger.addEventListener('click', (event) => {
                // Bez reloadu stránky: data už máme v řádku, AJAX je potřeba až při uložení.
                event.preventDefault();

                if (idField) idField.value = trigger.dataset.availabilityId || '0';
                setSelectValue(teamField, trigger.dataset.availabilityTeam || '');
                setSelectValue(statusField, trigger.dataset.availabilityStatus || 'injured');
                if (startsField) startsField.value = trigger.dataset.availabilityStarts || '';
                if (endsField) endsField.value = trigger.dataset.availabilityEnds || '';
                if (noteField) noteField.value = trigger.dataset.availabilityNote || '';
                if (activeField) activeField.checked = trigger.dataset.availabilityActive === '1';
                setMode(true);
            });
        });

        cancel?.addEventListener('click', (event) => {
            event.preventDefault();
            resetEditor();
        });

        // Po AJAX validační chybě zůstane hidden ID vyplněné, takže editor zachová správný režim.
        setMode(Number(idField?.value || 0) > 0);
    });
}

function initNotificationPolling(root = document) {
    const center = root.querySelector?.('[data-notification-center]')
        || document.querySelector('[data-notification-center]');
    if (!center) return;

    const pollUrl = center.dataset.notificationPollUrl;
    if (!pollUrl) return;

    if (window.__clasicoNotificationPolling) {
        window.__clasicoNotificationPolling.center = center;
        window.__clasicoNotificationPolling.url = pollUrl;
        window.__clasicoNotificationPolling.markAllUrl = center.dataset.notificationMarkAllUrl || '';
        return;
    }

    const state = {
        center,
        url: pollUrl,
        markAllUrl: center.dataset.notificationMarkAllUrl || '',
        timer: null,
        loading: false,
        mutating: false,
        lastLoadedAt: 0,
    };
    window.__clasicoNotificationPolling = state;

    const refresh = async (force = false) => {
        if (state.loading || state.mutating || document.hidden) return;
        if (!force && Date.now() - state.lastLoadedAt < 20_000) return;

        const current = document.querySelector('[data-notification-center]');
        if (!current) return;
        state.center = current;
        state.url = current.dataset.notificationPollUrl || state.url;
        state.markAllUrl = current.dataset.notificationMarkAllUrl || state.markAllUrl;
        state.loading = true;

        try {
            const response = await fetch(state.url, {
                method: 'GET',
                credentials: 'same-origin',
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
                cache: 'no-store',
            });
            if (!response.ok) return;

            const payload = await response.json();
            if (!payload?.loggedIn) return;
            renderNotificationState(state.center, payload);
            state.lastLoadedAt = Date.now();
        } catch (error) {
            console.debug('Notification poll failed.', error);
        } finally {
            state.loading = false;
        }
    };

    const mutate = async (url, trigger = null) => {
        if (!url || state.mutating) return;

        const current = document.querySelector('[data-notification-center]');
        if (!current) return;
        state.center = current;
        state.mutating = true;
        if (trigger) trigger.disabled = true;

        try {
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json',
                },
                cache: 'no-store',
            });
            if (!response.ok) return;

            const payload = await response.json();
            if (!payload?.loggedIn) return;
            renderNotificationState(state.center, payload);
            state.lastLoadedAt = Date.now();
        } catch (error) {
            console.debug('Notification update failed.', error);
        } finally {
            state.mutating = false;
            if (trigger?.isConnected) trigger.disabled = false;
        }
    };

    state.refresh = refresh;
    state.timer = window.setInterval(() => refresh(false), 45_000);

    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) refresh(true);
    });

    document.addEventListener('click', event => {
        const current = document.querySelector('[data-notification-center]');
        if (!current) return;

        const markRead = event.target.closest('[data-notification-mark-read]');
        if (markRead && current.contains(markRead)) {
            event.preventDefault();
            event.stopPropagation();
            mutate(markRead.dataset.notificationMarkReadUrl || '', markRead);
            return;
        }

        const markAll = event.target.closest('[data-notification-mark-all]');
        if (markAll && current.contains(markAll)) {
            event.preventDefault();
            event.stopPropagation();
            const url = current.dataset.notificationMarkAllUrl || state.markAllUrl;
            mutate(url, markAll);
            return;
        }

        if (event.target.closest('[data-notification-trigger]')) {
            refresh(false);
        }
    }, true);
}

function renderNotificationState(center, payload) {
    const unread = Math.max(0, Number(payload.unreadCount) || 0);
    const badge = center.querySelector('[data-notification-badge]');
    if (badge) {
        badge.textContent = unread > 99 ? '99+' : String(unread);
        badge.classList.toggle('hidden', unread < 1);
    }

    const summary = center.querySelector('[data-notification-summary]');
    if (summary) {
        summary.textContent = unread > 0 ? `${unread} nepřečtených` : 'Všechno přečteno';
    }

    const markAll = center.querySelector('[data-notification-mark-all]');
    if (markAll) {
        markAll.classList.toggle('hidden', unread < 1);
    }

    const list = center.querySelector('[data-notification-list]');
    if (!list || !Array.isArray(payload.items)) return;

    list.replaceChildren();
    if (payload.items.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'px-4 py-8 text-center';
        empty.dataset.notificationEmpty = '';

        const icon = document.createElement('i');
        icon.className = 'fa-duotone fa-solid fa-bell-slash text-xl text-slate-300 dark:text-slate-600';
        const label = document.createElement('div');
        label.className = 'mt-2 text-xs font-semibold text-slate-500';
        label.textContent = 'Zatím tu nic není.';
        empty.append(icon, label);
        list.append(empty);
        return;
    }

    payload.items.forEach(item => list.append(createNotificationItem(item)));
}

function createNotificationItem(item) {
    const wrapper = document.createElement('div');
    wrapper.dataset.notificationItem = String(item.id || '');
    wrapper.className = `relative border-b ui-divider text-slate-600 dark:text-slate-300 transition hover:bg-slate-100 dark:hover:bg-white/[0.035]${item.read ? '' : ' bg-rose-50 dark:bg-rose-950/10'}`;

    const link = document.createElement('a');
    link.href = String(item.openUrl || '#');
    link.className = `block px-3 py-2.5 hover:text-slate-900 dark:hover:text-white${item.read ? '' : ' pr-11'}`;

    const row = document.createElement('div');
    row.className = 'flex gap-2.5';

    const iconWrap = document.createElement('span');
    iconWrap.className = `mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md ${item.read ? 'bg-slate-100 text-slate-400 dark:bg-slate-800' : 'bg-rose-100 text-rose-600 dark:bg-rose-950/40'}`;
    const icon = document.createElement('i');
    const iconName = item.type === 'comment_reply'
        ? 'fa-reply'
        : (item.type === 'mention' ? 'fa-at' : (item.type === 'achievement_level_up' ? 'fa-trophy' : 'fa-comment'));
    icon.className = `fa-duotone fa-solid ${iconName} text-xs`;
    iconWrap.append(icon);

    const body = document.createElement('span');
    body.className = 'min-w-0 flex-1';
    const title = document.createElement('span');
    title.className = `block text-xs leading-4 ${item.read ? 'font-semibold text-slate-600 dark:text-slate-300' : 'font-bold text-slate-900 dark:text-slate-100'}`;
    title.textContent = String(item.message || 'Nové upozornění');
    body.append(title);

    if (item.excerpt) {
        const excerpt = document.createElement('span');
        excerpt.className = 'mt-0.5 block truncate text-[11px] text-slate-500 dark:text-slate-400';
        excerpt.textContent = String(item.excerpt);
        body.append(excerpt);
    }

    const time = document.createElement('span');
    time.className = 'mt-1 block text-[10px] text-slate-400';
    time.textContent = formatNotificationTime(item.createdAt);
    body.append(time);

    row.append(iconWrap, body);
    link.append(row);
    wrapper.append(link);

    if (!item.read && item.markReadUrl) {
        const markRead = document.createElement('button');
        markRead.type = 'button';
        markRead.dataset.notificationMarkRead = '';
        markRead.dataset.notificationMarkReadUrl = String(item.markReadUrl);
        markRead.className = 'absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-md text-slate-400 transition hover:bg-emerald-50 hover:text-emerald-600 dark:hover:bg-emerald-400/10 dark:hover:text-emerald-300';
        markRead.title = 'Označit jako přečtené';
        markRead.setAttribute('aria-label', 'Označit upozornění jako přečtené');

        const check = document.createElement('i');
        check.className = 'fa-duotone fa-solid fa-check text-xs';
        markRead.append(check);
        wrapper.append(markRead);
    }

    return wrapper;
}

function formatNotificationTime(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
    if (seconds < 60) return 'právě teď';
    if (seconds < 3600) return `před ${Math.floor(seconds / 60)} min`;
    if (seconds < 86400) return `před ${Math.floor(seconds / 3600)} h`;

    return new Intl.DateTimeFormat('cs-CZ', {
        day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(date);
}

function initNativeDropdowns(root = document) {
    const dropdowns = root.querySelectorAll?.('[data-native-dropdown]') || [];
    dropdowns.forEach((dropdown) => {
        if (dropdown.dataset.nativeDropdownReady === '1') return;
        dropdown.dataset.nativeDropdownReady = '1';

        dropdown.addEventListener('toggle', () => {
            if (!dropdown.open) return;
            document.querySelectorAll('[data-native-dropdown][open]').forEach((other) => {
                if (other !== dropdown) other.removeAttribute('open');
            });
        });
    });

    if (window.__clasicoNativeDropdownListenersInstalled) return;
    window.__clasicoNativeDropdownListenersInstalled = true;

    document.addEventListener('pointerdown', (event) => {
        const opened = document.querySelector('[data-native-dropdown][open]');
        if (!opened || opened.contains(event.target)) return;
        opened.removeAttribute('open');
    });

    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        document.querySelectorAll('[data-native-dropdown][open]').forEach((dropdown) => dropdown.removeAttribute('open'));
    });
}


function initRegistrationIdentityChecks(root = document) {
    const findInputs = () => {
        if (root?.matches?.('[data-identity-check]')) return [root];
        return Array.from(root?.querySelectorAll?.('[data-identity-check]') || []);
    };

    const inputs = findInputs();
    if (inputs.length === 0) return;

    if (!window.__clasicoIdentityCheckState) {
        window.__clasicoIdentityCheckState = {
            timers: new WeakMap(),
            controllers: new WeakMap(),
        };
    }

    const state = window.__clasicoIdentityCheckState;

    const applyState = (input, visualState, message) => {
        const type = input.dataset.identityCheck;
        const scope = input.closest('form') || document;
        const status = scope.querySelector(`[data-identity-status="${type}"]`);
        const iconWrap = scope.querySelector(`[data-identity-icon="${type}"]`);
        const icon = iconWrap?.querySelector('i');

        input.classList.add('ui-control');
        input.classList.remove('ui-control-success', 'ui-control-error');

        status?.classList.remove('text-slate-500', 'text-emerald-600', 'text-red-600', 'text-amber-600');

        if (visualState === 'ok') {
            input.classList.add('ui-control-success');
            status?.classList.add('text-emerald-600');
            if (iconWrap && icon) {
                iconWrap.classList.remove('hidden');
                iconWrap.classList.add('flex');
                icon.className = 'fa-duotone fa-solid fa-circle-check text-base text-emerald-500';
            }
        } else if (visualState === 'error') {
            input.classList.add('ui-control-error');
            status?.classList.add('text-red-600');
            if (iconWrap && icon) {
                iconWrap.classList.remove('hidden');
                iconWrap.classList.add('flex');
                icon.className = 'fa-duotone fa-solid fa-circle-xmark text-base text-red-500';
            }
        } else {
            input.classList.add('ui-control');
            status?.classList.add(visualState === 'loading' ? 'text-amber-600' : 'text-slate-500');
            if (iconWrap) {
                iconWrap.classList.add('hidden');
                iconWrap.classList.remove('flex');
            }
        }

        if (status && message) status.textContent = message;
    };

    const runCheck = async (input) => {
        const value = input.value.trim();
        const type = input.dataset.identityCheck;
        const endpoint = input.dataset.identityCheckUrl;
        if (!endpoint) return;

        if (!value) {
            applyState(input, 'idle', type === 'username'
                ? '3–30 znaků, bez diakritiky. Povolená je tečka, pomlčka a podtržítko.'
                : 'Není veřejný. Slouží pro ověření a obnovu hesla.');
            return;
        }

        state.controllers.get(input)?.abort();
        const controller = new AbortController();
        state.controllers.set(input, controller);
        applyState(input, 'loading', 'Kontroluji…');

        try {
            const url = new URL(endpoint, window.location.origin);
            url.searchParams.set(type, value);
            url.searchParams.set('_ts', String(Date.now()));

            const response = await window.fetch(url.toString(), {
                method: 'GET',
                credentials: 'same-origin',
                cache: 'no-store',
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                signal: controller.signal,
            });

            if (!response.ok) {
                throw new Error(`Identity check HTTP ${response.status}`);
            }

            const contentType = response.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
                throw new Error('Identity check did not return JSON');
            }

            const result = await response.json();
            const ok = result?.valid === true && result?.available === true;
            applyState(input, ok ? 'ok' : 'error', result?.message || (ok ? 'Hodnota je volná.' : 'Hodnotu nelze použít.'));
        } catch (error) {
            if (error?.name === 'AbortError') return;
            console.error('Registration identity check failed:', error, { type, endpoint });
            applyState(input, 'error', 'Kontrola dostupnosti selhala. Zkus stránku obnovit.');
        }
    };

    inputs.forEach((input) => {
        if (input.dataset.identityCheckReady === '1') return;
        input.dataset.identityCheckReady = '1';

        input.addEventListener('input', () => {
            const oldTimer = state.timers.get(input);
            if (oldTimer) window.clearTimeout(oldTimer);
            applyState(input, 'loading', 'Kontroluji…');
            state.timers.set(input, window.setTimeout(() => runCheck(input), 400));
        });

        input.addEventListener('blur', () => runCheck(input));

        if (input.value.trim() !== '') runCheck(input);
    });
}

/* =================================================================
   SPECIFICKÉ UI KOMPONENTY
================================================================= */


/**
 * Přechodový fallback pro obrázky po sjednocení media cest.
 * Databáze už obsahuje jen filename a nový kanonický root je /uploads/<typ>/.
 * Staré soutěže / vysílání mohly fyzicky zůstat v /assets/images/<typ>/.
 * Fallback pouze zobrazuje starý soubor; nic nemění v DB.
 */
function initMediaImageFallbacks(root = document) {
    const legacy = {
        competitions: '/assets/images/competitions/',
        broadcasts: '/assets/images/broadcasts/',
        players: '/assets/images/players/',
        stadiums: '/assets/images/stadiums/',
    };

    const images = root.querySelectorAll?.('img[src]') || [];
    images.forEach((img) => {
        if (img.dataset.mediaFallbackInitialized === '1') return;

        const src = img.getAttribute('src') || '';
        const match = src.match(/\/uploads\/(competitions|broadcasts|players|stadiums)\/([^/?#]+)(?:[?#].*)?$/i);
        if (!match) return;

        const type = String(match[1]).toLowerCase();
        const filename = match[2];
        const fallbackBase = legacy[type];
        if (!fallbackBase) return;

        img.dataset.mediaFallbackInitialized = '1';
        img.addEventListener('error', () => {
            if (img.dataset.mediaFallbackUsed === '1') return;
            img.dataset.mediaFallbackUsed = '1';
            img.src = `${fallbackBase}${filename}`;
        }, { once: false });
    });
}

/** * Tmavý režim (Dark Mode)
 * Zahrnuje ukládání do localStorage a přepínání ikonek.
 */
function initSeasonCompetitionSelects(root = document) {
    const scopes = root.querySelectorAll?.('[data-season-competition-map]') || [];

    scopes.forEach((scope) => {
        if (scope.dataset.seasonCompetitionInitialized === '1') return;

        let map = {};
        try {
            map = JSON.parse(scope.dataset.seasonCompetitionMap || '{}');
        } catch (error) {
            console.warn('Neplatná mapa sezóna → soutěže.', error);
            return;
        }

        const seasonSelect = scope.querySelector('[data-season-source]');
        const competitionSelect = scope.querySelector('[data-season-competitions]');
        if (!seasonSelect || !competitionSelect) return;

        const allOptions = Array.from(competitionSelect.options).map(option => ({
            value: String(option.value),
            text: option.textContent || '',
        }));

        const apply = () => {
            const seasonId = String(seasonSelect.value || '');
            const allowedObject = map[seasonId] || {};
            const allowedIds = new Set(Object.keys(allowedObject).map(String));
            const selected = Array.from(competitionSelect.selectedOptions).map(option => String(option.value));
            const nextSelected = selected.filter(value => allowedIds.has(value));

            const tom = competitionSelect.tomselect;
            if (tom) {
                tom.clear(true);
                tom.clearOptions();
                allOptions.forEach(option => {
                    if (allowedIds.has(option.value)) {
                        tom.addOption({ value: option.value, text: option.text });
                    }
                });
                tom.refreshOptions(false);
                if (nextSelected.length > 0) {
                    tom.setValue(nextSelected, true);
                }
            } else {
                Array.from(competitionSelect.options).forEach(option => {
                    option.hidden = !allowedIds.has(String(option.value));
                    option.disabled = !allowedIds.has(String(option.value));
                });
            }
        };

        seasonSelect.addEventListener('change', apply);
        seasonSelect.tomselect?.on?.('change', apply);
        scope.dataset.seasonCompetitionInitialized = '1';
        apply();
    });
}

function initHomeStadiumSelects(root = document) {
    const scopes = root.querySelectorAll?.('[data-stadium-home-map]') || [];
    scopes.forEach((scope) => {
        if (scope.dataset.stadiumHomeInitialized === '1') return;
        let map = {};
        try { map = JSON.parse(scope.dataset.stadiumHomeMap || '{}'); }
        catch (error) { console.warn('Neplatná mapa tým → stadion.', error); return; }
        const home = scope.querySelector('[data-home-team-source]');
        const stadium = scope.querySelector('[data-stadium-select]');
        const kickoff = scope.querySelector('[data-kickoff-source]');
        if (!home || !stadium) return;

        const setStadium = (value) => {
            const next = value ? String(value) : '';
            if (stadium.tomselect) stadium.tomselect.setValue(next, true);
            else stadium.value = next;
            stadium.dataset.autoStadiumValue = next;
        };

        const apply = () => {
            const teamId = String(home.value || '');
            const raw = map[teamId] ?? [];
            // Zpětná kompatibilita se starou mapou teamId => stadiumId.
            const assignments = Array.isArray(raw) ? raw : (raw ? [{ stadiumId: raw, from: null, to: null, legacy: true }] : []);
            const matchDate = String(kickoff?.value || '').slice(0, 10);

            let selected = null;
            if (matchDate) {
                selected = assignments.find((item) => {
                    const from = item?.from ? String(item.from) : null;
                    const to = item?.to ? String(item.to) : null;
                    return (!from || from <= matchDate) && (!to || to >= matchDate) && !item?.legacy;
                }) || assignments.find((item) => {
                    const from = item?.from ? String(item.from) : null;
                    const to = item?.to ? String(item.to) : null;
                    return (!from || from <= matchDate) && (!to || to >= matchDate);
                }) || null;
            } else {
                selected = assignments.find((item) => !item?.legacy && !item?.from && !item?.to)
                    || assignments.find((item) => item?.legacy)
                    || null;
            }

            const current = String(stadium.value || '');
            const previousAuto = String(stadium.dataset.autoStadiumValue || '');
            const wasManuallyChanged = current !== '' && current !== previousAuto;
            if (wasManuallyChanged) return;

            const stadiumId = selected?.stadiumId ? String(selected.stadiumId) : '';
            if (stadiumId !== current) setStadium(stadiumId);
        };

        const releaseAutoSelection = () => {
            const current = String(stadium.value || '');
            if (current !== String(stadium.dataset.autoStadiumValue || '')) {
                stadium.dataset.autoStadiumValue = '';
            }
        };

        const onHomeChange = () => {
            // Změna domácího týmu je nový kontext: i dříve ručně zvolený stadion
            // už nemusí dávat smysl, proto pro nový tým znovu použijeme automatiku.
            stadium.dataset.autoStadiumValue = String(stadium.value || '');
            apply();
        };
        home.addEventListener('change', onHomeChange);
        home.tomselect?.on?.('change', onHomeChange);
        kickoff?.addEventListener('change', apply);
        kickoff?.addEventListener('input', apply);
        stadium.addEventListener('change', releaseAutoSelection);
        stadium.tomselect?.on?.('change', releaseAutoSelection);
        scope.dataset.stadiumHomeInitialized = '1';
        apply();
    });
}

function initPlayerTabs(root = document) {
    const groups = root.querySelectorAll?.('[data-player-tabs]') || [];
    groups.forEach((group) => {
        if (group.dataset.playerTabsReady === '1') return;
        const buttons = Array.from(group.querySelectorAll('[data-player-tab]'));
        const container = group.closest('[data-player-tabs-shell]') || document;
        const triggers = Array.from(container.querySelectorAll('[data-player-tab]'));
        const panels = Array.from(container.querySelectorAll('[data-player-tab-panel]'));
        if (!buttons.length || !panels.length) return;

        const activate = (name, updateHash = false) => {
            const exists = buttons.some((button) => button.dataset.playerTab === name);
            const selected = exists ? name : (buttons[0]?.dataset.playerTab || 'prehled');
            buttons.forEach((button) => {
                const active = button.dataset.playerTab === selected;
                button.setAttribute('aria-selected', active ? 'true' : 'false');
                button.tabIndex = active ? 0 : -1;
            });
            panels.forEach((panel) => {
                panel.hidden = panel.dataset.playerTabPanel !== selected;
            });
            if (updateHash) {
                const hash = selected === 'prehled' ? '' : `#${selected}`;
                window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${hash}`);
            }
        };

        triggers.forEach((button) => {
            button.addEventListener('click', () => activate(button.dataset.playerTab || 'prehled', true));
        });

        buttons.forEach((button, index) => {
            button.addEventListener('keydown', (event) => {
                if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
                event.preventDefault();
                const step = event.key === 'ArrowRight' ? 1 : -1;
                const target = buttons[(index + step + buttons.length) % buttons.length];
                target?.focus();
                if (target) activate(target.dataset.playerTab || 'prehled', true);
            });
        });

        const initial = window.location.hash ? window.location.hash.slice(1) : 'prehled';
        activate(initial, false);
        group.dataset.playerTabsReady = '1';
    });
}

function initThemeToggle(root) {
    const html = document.documentElement;

    // Administrace má vlastní jednotný light režim. Nesmí číst ani měnit uživatelův
    // frontendový theme z localStorage, ani kdyby se sem někdy omylem dostal theme toggle.
    if (html.dataset.forceLight === '1' || document.body?.classList.contains('admin-light')) {
        html.classList.remove('dark');
        html.dataset.theme = 'light';
        html.style.colorScheme = 'light';
        return;
    }

    const toggleBtn = root.querySelector?.('#theme-toggle') || document.querySelector('#theme-toggle');

    if (!toggleBtn) return;
    if (toggleBtn.dataset.listenerAttached === '1') return;

    const darkIcon = document.querySelector('#theme-toggle-dark-icon');
    const lightIcon = document.querySelector('#theme-toggle-light-icon');
    const tooltipTheme = document.querySelector('#tooltip-theme');
    const themeColorMeta = document.querySelector('#theme-color-meta');

    const updateThemeUi = () => {
        const isDark = html.classList.contains('dark');
        const theme = isDark ? 'dark' : 'light';

        // V dark režimu nabízíme slunce, ve světlém měsíc.
        // Ikony se zobrazují čistě přes CSS podle html[data-theme], takže při načtení neprobliknou.

        const label = isDark ? 'Přepnout na světlý režim' : 'Přepnout na tmavý režim';
        toggleBtn.setAttribute('aria-label', label);
        toggleBtn.setAttribute('title', label);
        toggleBtn.setAttribute('aria-pressed', isDark ? 'true' : 'false');

        html.dataset.theme = theme;
        html.style.colorScheme = theme;

        if (themeColorMeta) {
            const color = isDark
                ? themeColorMeta.dataset.themeColorDark
                : themeColorMeta.dataset.themeColorLight;
            if (color) themeColorMeta.setAttribute('content', color);
        }
    };

    const setTheme = (theme, persist = true) => {
        const normalizedTheme = theme === 'light' ? 'light' : 'dark';
        html.classList.toggle('dark', normalizedTheme === 'dark');

        if (persist) {
            try {
                window.localStorage.setItem('theme', normalizedTheme);
                window.localStorage.removeItem('clasico_theme');
            } catch (error) {
                // Storage nemusí být vždy dostupné; samotné přepnutí ale funguje dál.
            }
        }

        updateThemeUi();
        window.dispatchEvent(new CustomEvent('clasicoThemeChanged', {
            detail: { theme: normalizedTheme },
        }));
    };

    toggleBtn.addEventListener('click', () => {
        setTheme(html.classList.contains('dark') ? 'light' : 'dark');
    });

    ['mouseenter', 'focus'].forEach((eventName) => {
        toggleBtn.addEventListener(eventName, () => tooltipTheme?.classList.remove('invisible', 'opacity-0'));
    });
    ['mouseleave', 'blur'].forEach((eventName) => {
        toggleBtn.addEventListener(eventName, () => tooltipTheme?.classList.add('invisible', 'opacity-0'));
    });

    toggleBtn.dataset.listenerAttached = '1';
    toggleBtn.classList.remove('hidden');
    updateThemeUi();
}

/** * Hamburger Menu (Mobile)
 * Sleduje změny v DOMu (MutationObserver), aby poznalo, kdy se otevře drawer.
 */
function initHamburger(root) {
    // Pokud už observer běží, nic neděláme (běží globálně pro celou session)
    if (window.hamburgerObserverInitialized) return;

    const btn = document.querySelector('#toggle-hamburger-btn');
    if (!btn) return;

    const hamburger = document.querySelector('#toggle-hamburger-btn-open');
    const close = document.querySelector('#toggle-hamburger-btn-close');

    const setOpen = () => { hamburger?.classList.add('hidden'); close?.classList.remove('hidden'); };
    const setClosed = () => { hamburger?.classList.remove('hidden'); close?.classList.add('hidden'); };

    // Sledujeme, jestli Flowbite přidal backdrop (tmavé pozadí), což značí otevřené menu
    new MutationObserver(mutations => {
        mutations.forEach(m => {
            m.addedNodes.forEach(n => { if (n.nodeType === 1 && n.hasAttribute?.('drawer-backdrop')) setOpen(); });
            m.removedNodes.forEach(n => { if (n.nodeType === 1 && n.hasAttribute?.('drawer-backdrop')) setClosed(); });
        });
    }).observe(document.body, { childList: true, subtree: true });

    window.hamburgerObserverInitialized = true;
    setClosed();
}

/** * Kopírování do schránky (Clipboard)
 */
function initClipboardButtons(root) {
    const buttons = root.querySelectorAll('.copy-element-inside .copy-btn');

    buttons.forEach(btn => {
        if (btn.dataset.clipboardInitialized) return; // Ochrana proti duplicitě
        btn.dataset.clipboardInitialized = 'true';

        btn.addEventListener('click', function() {
            const val = this.parentNode.querySelector('.copy-value')?.value || this.parentNode.querySelector('.copy-value')?.textContent || '';
            const icon = this.querySelector('i');

            // Vizuální efekt (fajfka)
            const animateSuccess = () => {
                icon.classList.remove('fa-clipboard');
                icon.classList.add('fa-check');
                setTimeout(() => {
                    icon.classList.remove('fa-check');
                    icon.classList.add('fa-clipboard');
                }, 2000);
            };

            // Zápis do schránky
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(val).then(animateSuccess);
            } else {
                // Fallback pro starší prohlížeče
                const t = document.createElement('textarea');
                t.value = val;
                document.body.appendChild(t);
                t.select();
                document.execCommand('copy');
                document.body.removeChild(t);
                animateSuccess();
            }
        });
    });
}


function initReferralCopyFeedback(root = document) {
    const buttons = root.querySelectorAll?.('[data-referral-copy-feedback]') || [];

    buttons.forEach((button) => {
        if (button.dataset.referralCopyFeedbackInitialized) return;
        button.dataset.referralCopyFeedbackInitialized = 'true';

        button.addEventListener('click', () => {
            const defaultState = button.querySelector('[data-copy-default]');
            const successState = button.querySelector('[data-copy-success]');
            const tooltipId = button.dataset.tooltipTarget;
            const tooltip = tooltipId ? document.getElementById(tooltipId) : null;
            const tooltipDefault = tooltip?.querySelector('[data-copy-tooltip-default]');
            const tooltipSuccess = tooltip?.querySelector('[data-copy-tooltip-success]');

            // Samotné kopírování zajišťuje Flowbite přes data-copy-to-clipboard-target.
            // Tady řešíme pouze jasnou odezvu pro uživatele.
            window.setTimeout(() => {
                // Tailwind: nenecháváme na prvku zároveň `hidden` a `inline-flex`.
                // Podle pořadí utilit v buildu by jinak mohlo `inline-flex` přebít `hidden`
                // a zobrazily by se oba stavy tlačítka současně.
                defaultState?.classList.remove('inline-flex');
                defaultState?.classList.add('hidden');

                successState?.classList.remove('hidden');
                successState?.classList.add('inline-flex');

                tooltipDefault?.classList.add('hidden');
                tooltipSuccess?.classList.remove('hidden');

                showAjaxFlash('Odkaz byl zkopírován do schránky.', 'success');

                if (button.__referralCopyFeedbackTimer) {
                    window.clearTimeout(button.__referralCopyFeedbackTimer);
                }

                button.__referralCopyFeedbackTimer = window.setTimeout(() => {
                    successState?.classList.remove('inline-flex');
                    successState?.classList.add('hidden');

                    defaultState?.classList.remove('hidden');
                    defaultState?.classList.add('inline-flex');

                    tooltipDefault?.classList.remove('hidden');
                    tooltipSuccess?.classList.add('hidden');
                    button.__referralCopyFeedbackTimer = null;
                }, 1800);
            }, 0);
        });
    });
}



/* =================================================================
   PLYNULÉ LIVE HODINY
   - stav držíme mimo DOM, takže AJAX redraw hlavičky čas neresetuje
   - phase je součást synchronizace: přechod 1. -> 2. poločas se srovná
   - větší ruční korekce z administrace se převezme ze serveru
================================================================= */
function initLiveClocks(root = document) {
    if (!window.__clasicoLiveClockStates) {
        window.__clasicoLiveClockStates = new Map();
    }

    const states = window.__clasicoLiveClockStates;
    const now = Date.now();

    const elements = root.querySelectorAll?.('[data-live-clock]') || [];
    elements.forEach((el) => {
        const key = el.dataset.clockKey || `clock-${Math.random().toString(36).slice(2)}`;
        const incomingSeconds = Math.max(0, Number(el.dataset.seconds || 0));
        const running = el.dataset.clockRunning !== '0';
        const phase = el.dataset.clockPhase || '';
        const syncToken = el.dataset.clockSyncToken || `${phase}:${running ? '1' : '0'}`;
        const serverTimeMsRaw = Number(el.dataset.serverTimeMs || now);
        const serverTimeMs = Number.isFinite(serverTimeMsRaw) ? serverTimeMsRaw : now;
        const incomingProjectedSeconds = Math.max(
            0,
            incomingSeconds + (running ? Math.max(0, Math.floor((now - serverTimeMs) / 1000)) : 0),
        );
        let state = states.get(key);

        const projected = state
            ? state.baseSeconds + (state.running ? Math.floor((now - state.syncedAt) / 1000) : 0)
            : incomingProjectedSeconds;

        // Při běžném AJAX pollingu tolerujeme malý rozdíl server/klient.
        // Díky tomu čas "necukne" zpět nebo dopředu o sekundu při každém redraw.
        const newerServerSnapshot = !state || serverTimeMs > (state.lastServerTimeMs || 0);
        const mustResync = !state
            || state.phase !== phase
            || state.running !== running
            || state.syncToken !== syncToken;

        if (mustResync) {
            state = {
                baseSeconds: incomingProjectedSeconds,
                syncedAt: now,
                running,
                phase,
                syncToken,
                lastServerTimeMs: serverTimeMs,
                lastSeenAt: now,
            };
            states.set(key, state);
        } else {
            // Běžný polling nesmí lokální plynulé hodiny vracet zpět. Novější
            // serverový snapshot použijeme jen pro malou dopřednou korekci; ruční
            // změna času/phase vytvoří nový syncToken a projde větví výše.
            if (newerServerSnapshot && incomingProjectedSeconds > projected + 2) {
                state.baseSeconds = incomingProjectedSeconds;
                state.syncedAt = now;
            }
            if (newerServerSnapshot) state.lastServerTimeMs = serverTimeMs;
            state.lastSeenAt = now;
        }

        el.dataset.clockKey = key;
    });

    const render = () => {
        const tickNow = Date.now();
        document.querySelectorAll('[data-live-clock]').forEach((el) => {
            const key = el.dataset.clockKey;
            const state = key ? states.get(key) : null;
            if (!state) return;

            const seconds = Math.max(
                0,
                state.baseSeconds + (state.running ? Math.floor((tickNow - state.syncedAt) / 1000) : 0),
            );

            const min = Math.floor(seconds / 60);
            const sec = seconds % 60;
            el.dataset.currentSeconds = String(seconds);
            el.textContent = `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
        });
    };

    render();

    if (!window.__clasicoLiveClockTimer) {
        window.__clasicoLiveClockTimer = window.setInterval(render, 500);
    }
}

export function initLazyLoads() {
    const lazyBlocks = document.querySelectorAll('.js-lazy-load');

    lazyBlocks.forEach(block => {
        const url = block.dataset.src;
        if (!url) return;

        // Použijeme fetch (univerzální) nebo naja.makeRequest
        fetch(url, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })
            .then(response => response.text())
            .then(html => {
                block.innerHTML = html;
                block.classList.remove('animate-pulse');
                // Pokud je potřeba, reinicializujeme komponenty uvnitř (Flowbite atd.)
                // initAllPageComponents(block);
            })
            .catch(err => console.error('Chyba načítání bloku:', err));
    });
}

/* =================================================================
   UNIVERSAL FLASH MESSAGES
   Jediný systém pro:
   - klasické Nette $flashes
   - AJAX payload.flash
================================================================= */

// =========================
// NASTAVENÍ AUTO-CLOSE
// ZMĚŇ TUTO HODNOTU:
// =========================
const FLASH_AUTO_CLOSE_MS = 4200;

function initUniversalFlashMessages(root = document) {
    root.querySelectorAll('[data-flash-message]').forEach(item => {
        bindUniversalFlashItem(item);
    });
}

function bindUniversalFlashItem(item) {
    if (!item || item.dataset.flashInitialized === '1') return;

    item.dataset.flashInitialized = '1';

    const close = item.querySelector('[data-flash-close]');
    const timer = item.querySelector('[data-flash-timer]');

    let timeoutId = null;
    let closing = false;

    const remove = () => {
        if (closing) return;
        closing = true;

        if (timeoutId !== null) {
            window.clearTimeout(timeoutId);
        }

        item.style.transition =
            'opacity 180ms ease, transform 180ms ease, max-height 220ms ease, margin 220ms ease';

        item.style.opacity = '0';
        item.style.transform = 'translateY(-4px)';
        item.style.maxHeight = `${item.offsetHeight}px`;
        item.style.overflow = 'hidden';

        window.requestAnimationFrame(() => {
            item.style.maxHeight = '0';
            item.style.marginTop = '0';
            item.style.marginBottom = '0';
        });

        window.setTimeout(() => {
            item.remove();
        }, 230);
    };

    close?.addEventListener('click', event => {
        event.preventDefault();
        remove();
    });

    if (timer) {
        /*
         * Timer používá stejný DOM u Nette i AJAX zprávy.
         * Nezávisíme na druhém toast systému.
         */
        timer.style.transformOrigin = 'left center';
        timer.style.transform = 'scaleX(1)';
        timer.style.transition = 'none';

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                timer.style.transition =
                    `transform ${FLASH_AUTO_CLOSE_MS}ms linear`;
                timer.style.transform = 'scaleX(0)';
            });
        });
    }

    timeoutId = window.setTimeout(remove, FLASH_AUTO_CLOSE_MS);

    /*
     * Když uživatel najede na flash, timer se nezastavuje vizuálně,
     * ale zpráva nebude omylem zavřená během interakce.
     * Po odjetí dostane krátký nový interval.
     */
    item.addEventListener('mouseenter', () => {
        if (timeoutId !== null) {
            window.clearTimeout(timeoutId);
            timeoutId = null;
        }
    });

    item.addEventListener('mouseleave', () => {
        if (closing || timeoutId !== null) return;

        timeoutId = window.setTimeout(remove, 1400);
    });
}

function createUniversalFlashItem(message, type = 'info') {
    const normalizedType = ['success', 'error', 'warning', 'info'].includes(type)
        ? type
        : 'info';

    const icon = normalizedType === 'success'
        ? 'fa-circle-check'
        : normalizedType === 'error'
            ? 'fa-circle-exclamation'
            : normalizedType === 'warning'
                ? 'fa-triangle-exclamation'
                : 'fa-circle-info';

    const item = document.createElement('div');

    item.className = 'flash-message-item pointer-events-auto';
    item.dataset.flashMessage = '1';
    item.dataset.flashType = normalizedType;
    item.setAttribute('role', 'status');

    /*
     * Samotný message text nikdy nevkládáme přes innerHTML.
     * HTML je pouze naše pevná komponenta.
     */
    item.innerHTML = `
        <div class="flash-message-inner">
            <div class="flash-message-icon" aria-hidden="true">
                <i class="fa-duotone fa-solid ${icon}"></i>
            </div>

            <div class="flash-message-text" data-flash-text></div>

            <button
                type="button"
                class="flash-message-close"
                data-flash-close
                aria-label="Zavřít zprávu"
                title="Zavřít"
            >
                <i class="fa-duotone fa-solid fa-xmark"></i>
            </button>
        </div>

        <div
            class="flash-message-timer"
            data-flash-timer
            aria-hidden="true"
        ></div>
    `;

    item.querySelector('[data-flash-text]').textContent = message;

    return item;
}

export function showAjaxFlash(message, type = 'info') {
    if (!message) return;

    /*
     * AJAX už nevytváří vlastní toast host.
     * Použije přesně stejný host jako Nette flashMessage().
     */
    const host = document.querySelector('[data-flash-host], #flash-messages');

    if (!host) {
        console.warn('Universal flash host #flash-messages nebyl nalezen.');
        return;
    }

    const item = createUniversalFlashItem(message, type);

    host.appendChild(item);
    bindUniversalFlashItem(item);
}
